import { AxiosError } from 'axios';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { ExperienceDataType } from '../../../../models/dashboard/settings/profileSettings/experience.model';
import {
  ErrorHttpResponse,
  HttpConfig,
  SuccessHttpResponse,
} from '../../../../models/https';
import useHttps from '../../../useHttps';
import useGetUserData from '../useGetUserData';
import { employmentTypeDict } from '../../../../utils/utils';

const useEditExperience = () => {
  const request = useHttps();
  const [loading, setLoading] = useState(false);
  const { getUserData } = useGetUserData();

  const onEditExperienceError = (err: AxiosError<ErrorHttpResponse>) => {
    toast.error(
      err?.response?.data?.message ??
        'Something went wrong while updating experience data'
    );
  };

  const onEditExperienceSuccess =
    (resetFormFn: (...args: any) => void) =>
    ({ data, message }: SuccessHttpResponse<any>) => {
      toast.success(message);
      getUserData(); // Refresh user data to reflect changes
      resetFormFn();
    };

  const editExperience = (
    experienceId: string,
    updatedData: Partial<ExperienceDataType>,
    resetFormFn: (...args: any) => void
  ) => {
    const formData = new FormData();

    // Loop over updatedData entries and append all except employmentType
    Object.entries(updatedData).forEach(([key, value]) => {
      // Skip the employmentType as we'll handle it separately
      if (key === 'employmentType') return;
      if (value !== undefined && value !== null) {
        if (value instanceof Date) {
          formData.append(key, value.toISOString());
        } else if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, value as string);
        }
      }
    });

    // Handle employmentType mapping explicitly
    if (updatedData.employmentType) {
      formData.append(
        'employmentType',
        employmentTypeDict[updatedData.employmentType]
      );
    }

    const config: HttpConfig = {
      url: `auth-service/users/experiences/${experienceId}`,
      method: 'put',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    request(
      config,
      onEditExperienceSuccess(resetFormFn),
      setLoading,
      onEditExperienceError
    );
  };

  return { editExperience, loading };
};

export default useEditExperience;
